import { PAGE_ACTION_KEYS, PAGE_KEYS } from '@rendora/common/entities/app_cfg';
import IconAccountFill from '@rendora/design/assets/account-fill.svg?component';
import IconAccount from '@rendora/design/assets/account.svg?component';
import IconConsumeFill from '@rendora/design/assets/consume-fill.svg?component';
import IconConsume from '@rendora/design/assets/consume.svg?component';
import IconEquityFill from '@rendora/design/assets/equity-fill.svg?component';
import IconEquity from '@rendora/design/assets/equity.svg?component';
import IconOrderFill from '@rendora/design/assets/order-fill.svg?component';
import IconOrder from '@rendora/design/assets/order.svg?component';
import IconOrganizationFill from '@rendora/design/assets/organization-fill.svg?component';
import IconOrganization from '@rendora/design/assets/organization.svg?component';

import { Component, computed, ComputedRef } from 'vue';

import { storeToRefs } from 'pinia';
import { RouterLinkProps } from 'vue-router';
import { getI18nService } from '../services/i18n';
import { usePermissionStore } from '../store/permission';
import { useUserStore } from '../store/user';
import { IRightsType, USER_ADMIN } from '../types/enums';
import { getPlatformJumpLink } from '../utils/url';

// 通用导航项接口
export interface INavItem {
  href?: string;
  to?: RouterLinkProps['to'];
  label: string;
  icon?: Component;
  iconFill?: Component;
  upgrade?: ComputedRef<boolean>;
  /** 侧边栏展开时展示的角标，如 'new' */
  badge?: string;
  rights_type?: IRightsType[];
  permission?: USER_ADMIN[];
  inVisible?: () => boolean;
}

export const useCenterNav = (isDropdown = false) => {
  const userStore = useUserStore();
  const permissionStore = usePermissionStore();
  const { hasPagePermission, hasActionPermission } = permissionStore;
  const { isTeamRight, user, isAdmin } = storeToRefs(userStore);

  const patchDomain = (path: string) => {
    return path;
  };

  const centerNavList = computed(() => {
    const i18nService = getI18nService();
    const $t = i18nService.t;
    const baseMenuItems: INavItem[] = [
      {
        label: $t('zhang-hao-she-zhi'),
        href: patchDomain('/center/account'),
        icon: IconAccount,
        iconFill: IconAccountFill,
        inVisible: () => {
          return !hasPagePermission('center-account');
        },
      },
      {
        label: $t('tuan-dui-guan-li'),
        href: patchDomain('/center/member'),
        icon: IconOrganization,
        iconFill: IconOrganizationFill,
        inVisible: () => {
          return !hasPagePermission('center-member');
        },
        rights_type: [
          IRightsType.ORG_FLAGSHIP,
          IRightsType.ORG_STANDARD,
          IRightsType.TEAM_LIGHT,
          IRightsType.TEAM_TASTE,
        ],
        permission: [
          USER_ADMIN.ORG_ADMIN_USER,
          USER_ADMIN.SUPER_ADMIN,
          USER_ADMIN.MANAGEMENT_PLATFORM,
        ],
      },
      {
        label: $t('xiao-hao-ji-lu'),
        href: patchDomain('/center/consume'),
        icon: IconConsume,
        iconFill: IconConsumeFill,
        inVisible: () => {
          return !hasPagePermission('center-consume');
        },
        rights_type: [
          IRightsType.PERSONAL,
          IRightsType.TASTE,
          IRightsType.TEAM_LIGHT,
          IRightsType.TEAM_TASTE,
          IRightsType.PERSONAL_LITE,
          IRightsType.ORG_FLAGSHIP,
          IRightsType.ORG_STANDARD,
        ],
        permission: [USER_ADMIN.NORMAL],
      },
      {
        label: $t('quan-yi-guan-li'),
        href: patchDomain('/center/equity'),
        icon: IconEquity,
        iconFill: IconEquityFill,
        inVisible: () => {
          return !hasPagePermission('center-equity');
        },
        rights_type: [
          IRightsType.PERSONAL,
          IRightsType.TASTE,
          IRightsType.ORG_FLAGSHIP,
          IRightsType.ORG_STANDARD,
          IRightsType.TEAM_LIGHT,
          IRightsType.TEAM_TASTE,
          IRightsType.PERSONAL_LITE,
        ],
        permission: [
          USER_ADMIN.ORG_ADMIN_USER,
          USER_ADMIN.MANAGEMENT_PLATFORM,
          USER_ADMIN.SUPER_ADMIN,
        ], // 企业管理员、后台管理员、超级管理员
      },
      {
        label: $t('ding-dan-fa-piao'),
        href: patchDomain('/center/order'),
        icon: IconOrder,
        iconFill: IconOrderFill,
        inVisible: () => {
          return !hasPagePermission('center-order');
        },
        rights_type: [
          IRightsType.PERSONAL,
          IRightsType.TASTE,
          IRightsType.ORG_FLAGSHIP,
          IRightsType.ORG_STANDARD,
          IRightsType.TEAM_LIGHT,
          IRightsType.TEAM_TASTE,
          IRightsType.PERSONAL_LITE,
        ],
        permission: [
          USER_ADMIN.ORG_ADMIN_USER,
          USER_ADMIN.MANAGEMENT_PLATFORM,
          USER_ADMIN.SUPER_ADMIN,
        ], // 企业管理员、后台管理员、超级管理员
      },
    ];

    if (isDropdown) {
      baseMenuItems.push({
        label: isTeamRight.value ? $t('tuan-dui-zhu-ye') : $t('ge-ren-zhu-ye'),
        href: getPlatformJumpLink(`/personal-page/${user.value?.organization_id}.html`),
        inVisible: () => {
          if (!hasPagePermission(PAGE_KEYS.PERSONAL_PAGE)) {
            return true;
          }
          return !hasActionPermission(PAGE_ACTION_KEYS.PERSONAL_PAGE_MEMBER_VIEW) && !isAdmin.value;
        },
      });
    }

    // 过滤菜单项，应用权限检查
    return baseMenuItems.filter(item => {
      // 检查用户等级权限
      if (item.rights_type && user.value?.rights_type) {
        const userRightsType = Number(user.value.rights_type);
        if (!item.rights_type.includes(userRightsType)) {
          return false;
        }
      }

      // 检查用户角色权限
      if (item.permission && user.value?.permission !== undefined && isTeamRight.value) {
        if (!item.permission.includes(user.value?.permission!)) {
          return false;
        }
      }

      return true;
    });
  });
  return {
    centerNavList,
  };
};
